import React, { useState } from "react";

const emissionFactors = {
  venue: {
    "Local Indoor Venue": 100,
    "Local Outdoor Venue": 80,
    "Destination Wedding": 500,
  },
  travel: {
    "Mostly Local Guests": 50,
    "Domestic Travel": 200,
    "International Travel": 500,
  },
  catering: {
    "Plant-Based Menu": 2,
    "Mixed Diet Menu": 5,
    "Meat-Heavy Menu": 10,
  },
  decorations: {
    "Sustainable/Recyclable Decorations": 20,
    "Locally Sourced Flowers": 50,
    "Single-Use Non-Recyclable Decorations": 100,
  },
  attire: {
    "Eco-Friendly/Sustainable Attire": 20,
    "Rented Attire": 30,
    "New, Single-Use Attire": 100,
  },
  invitations: {
    "Digital Invitations": 5,
    "Recycled Paper Invitations": 20,
    "Standard Paper Invitations": 50,
  },
  favors: {
    "Eco-Friendly Favors": 5,
    "Consumable Favors": 10,
    "Single-Use Favors": 20,
  },
};

const WeddingForm = () => {
  const [formData, setFormData] = useState({
    venue: "",
    travel: "",
    catering: "",
    decorations: "",
    attire: "",
    invitations: "",
    favors: "",
    guests: 0,
  });

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(true);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const isFormValid = () => {
    return Object.values(formData).every(
      (value) => value !== "" && value !== 0
    );
  };

  const calculateCarbonFootprint = () => {
    const venueCO2 = emissionFactors.venue[formData.venue] || 0;
    const travelCO2 =
      (emissionFactors.travel[formData.travel] || 0) * formData.guests;
    const cateringCO2 =
      (emissionFactors.catering[formData.catering] || 0) * formData.guests;
    const decorationsCO2 =
      emissionFactors.decorations[formData.decorations] || 0;
    const attireCO2 = (emissionFactors.attire[formData.attire] || 0) * 2; // Assuming 2 people (bride and groom)
    const invitationsCO2 =
      emissionFactors.invitations[formData.invitations] || 0;
    const favorsCO2 =
      (emissionFactors.favors[formData.favors] || 0) * formData.guests;

    const totalCO2 =
      venueCO2 +
      travelCO2 +
      cateringCO2 +
      decorationsCO2 +
      attireCO2 +
      invitationsCO2 +
      favorsCO2;
    return totalCO2;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isFormValid()) {
      alert("Please fill out all fields before submitting the form.");
      return;
    }
    setLoading(true);
    const totalCO2 = calculateCarbonFootprint();
    const treesNeeded = Math.ceil(totalCO2 / 20); // Assuming each tree offsets 20 kg CO2 per year
    const estimatedCost = treesNeeded * 4; // Assuming average cost is $4 AUD per tree

    const formattedResult = [
      `Total CO2 Emissions: ${totalCO2} kg`,
      `Number of Trees Needed: ${treesNeeded}`,
      `Estimated Cost: $${estimatedCost} AUD`,
    ];

    setResult(formattedResult);
    setLoading(false);
    setShowForm(false); // Hide the form after submission
  };

  const handleReuseForm = () => {
    setShowForm(true);
    setResult(null);
  };

  return (
    <div>
      {showForm ? (
        <form onSubmit={handleSubmit}>
          <label>
            Venue:
            <select name="venue" onChange={handleChange}>
              <option value="">Select</option>
              <option value="Local Indoor Venue">Local Indoor Venue</option>
              <option value="Local Outdoor Venue">Local Outdoor Venue</option>
              <option value="Destination Wedding">Destination Wedding</option>
            </select>
          </label>
          <label>
            Guest Travel:
            <select name="travel" onChange={handleChange}>
              <option value="">Select</option>
              <option value="Mostly Local Guests">Mostly Local Guests</option>
              <option value="Domestic Travel">Domestic Travel</option>
              <option value="International Travel">International Travel</option>
            </select>
          </label>
          <label>
            Catering:
            <select name="catering" onChange={handleChange}>
              <option value="">Select</option>
              <option value="Plant-Based Menu">Plant-Based Menu</option>
              <option value="Mixed Diet Menu">Mixed Diet Menu</option>
              <option value="Meat-Heavy Menu">Meat-Heavy Menu</option>
            </select>
          </label>
          <label>
            Decorations:
            <select name="decorations" onChange={handleChange}>
              <option value="">Select</option>
              <option value="Sustainable/Recyclable Decorations">
                Sustainable/Recyclable Decorations
              </option>
              <option value="Locally Sourced Flowers">
                Locally Sourced Flowers
              </option>
              <option value="Single-Use Non-Recyclable Decorations">
                Single-Use Non-Recyclable Decorations
              </option>
            </select>
          </label>
          <label>
            Wedding Attire:
            <select name="attire" onChange={handleChange}>
              <option value="">Select</option>
              <option value="Eco-Friendly/Sustainable Attire">
                Eco-Friendly/Sustainable Attire
              </option>
              <option value="Rented Attire">Rented Attire</option>
              <option value="New, Single-Use Attire">
                New, Single-Use Attire
              </option>
            </select>
          </label>
          <label>
            Invitations and Stationery:
            <select name="invitations" onChange={handleChange}>
              <option value="">Select</option>
              <option value="Digital Invitations">Digital Invitations</option>
              <option value="Recycled Paper Invitations">
                Recycled Paper Invitations
              </option>
              <option value="Standard Paper Invitations">
                Standard Paper Invitations
              </option>
            </select>
          </label>
          <label>
            Gifts and Favors:
            <select name="favors" onChange={handleChange}>
              <option value="">Select</option>
              <option value="Eco-Friendly Favors">Eco-Friendly Favors</option>
              <option value="Consumable Favors">Consumable Favors</option>
              <option value="Single-Use Favors">Single-Use Favors</option>
            </select>
          </label>
          <label>
            Number of Guests:
            <input type="number" name="guests" onChange={handleChange} />
          </label>
          <button type="submit" disabled={!isFormValid()}>
            Calculate Carbon Footprint
          </button>
        </form>
      ) : (
        <a href="#" className="reuse-form" onClick={handleReuseForm}>
          Click here to reuse the form
        </a>
      )}
      {loading && <div className="spinner"></div>}
      {result && (
        <section className="result-group">
          <div className="results">
            <h2>Carbon Footprint Result</h2>
            {result.map((line, index) => (
              <p key={index}>{line}</p>
            ))}
            <p>
              Plant trees and offset your carbon footprint. Here are some links
              to tree planting websites:
            </p>
            <ul>
              <li>
                <a
                  href="https://onetreeplanted.org/products/australia"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  One Tree Planted Australia
                </a>
              </li>
              <li>
                <a
                  href="https://www.reforestnow.org.au/planttrees"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  ReForest Now
                </a>
              </li>
              <li>
                <a
                  href="https://www.treesforcities.org/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Trees for Cities
                </a>
              </li>
            </ul>
          </div>
        </section>
      )}
      <h3 className="partner-heading">Partners:</h3>
      <div className="partners">
        <a href="https://ozwed.au/" target="_blank" rel="noopener noreferrer">
          <img
            src="https://ozwed.au/wp-content/uploads/2024/05/OzWeddingDirectory_Logo_BLACK_NoBG.png"
            alt="Oz Wedding Directory"
            className="partner-logo"
          />
        </a>
      </div>
    </div>
  );
};

export default WeddingForm;
